/**
 * Global quintile thresholds and colors for choropleth maps (counties + cities on one scale).
 */

/** Green → yellow → red (5 classes). */
export const CHOROPLETH_QUINTILE_COLORS = [
  '#1a9850',
  '#91cf60',
  '#ffffbf',
  '#fc8d59',
  '#d73027',
] as const;

export const CHOROPLETH_NO_DATA_FILL = '#e2e8f0';

/**
 * Four thresholds dividing sorted positive values into five quintile bands.
 * Returns null if there are no usable values.
 */
export function getQuintileThresholds(values: number[]): number[] | null {
  const v = values
    .filter((x) => x != null && Number.isFinite(x) && x > 0)
    .slice()
    .sort((a, b) => a - b);
  if (v.length === 0) return null;
  const q = (p: number) => v[Math.min(v.length - 1, Math.max(0, Math.floor(p * v.length)))];
  return [q(0.2), q(0.4), q(0.6), q(0.8)];
}

/** 0..4 quintile index; invalid / non-positive values → null (use {@link CHOROPLETH_NO_DATA_FILL}). */
export function getQuintileIndex(value: number | null | undefined, quintiles: number[]): number | null {
  if (value == null || !Number.isFinite(value) || value <= 0) return null;
  if (quintiles.length < 4) return null;
  const [q0, q1, q2, q3] = quintiles;
  if (value <= q0) return 0;
  if (value <= q1) return 1;
  if (value <= q2) return 2;
  if (value <= q3) return 3;
  return 4;
}

export function getQuintileColor(quintileIndex: number | null): string {
  if (quintileIndex == null || quintileIndex < 0 || quintileIndex > 4) return CHOROPLETH_NO_DATA_FILL;
  return CHOROPLETH_QUINTILE_COLORS[quintileIndex] ?? CHOROPLETH_NO_DATA_FILL;
}
